/**
 * usePrivateEvent — React Query hooks for the private-event detail page.
 *
 * `usePrivateEvent` loads the detail + crew by id. `useReplacePrivateEventCrew`
 * assigns/removes crew through the EXISTING workshop staffing endpoint (no new
 * staffing backend for a private event, it's a `Workshop` under the hood) and
 * then REFETCHES the private-event detail, because the PUT returns a
 * workshop-shaped body, not the private-event one the page renders from.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchPrivateEvent, fetchPrivateEventCrewView } from '../../api/privateEvents';
import { replaceWorkshopStaff, type CrewSlotInput } from '../../api/workshops';

const STALE_30S = 30_000;

export const privateEventKey = (id: string) => ['private-event', id] as const;

export function usePrivateEvent(id: string) {
  return useQuery({
    queryKey: privateEventKey(id),
    queryFn: () => fetchPrivateEvent(id),
    enabled: id.length > 0,
    staleTime: STALE_30S,
    // A 404 is a genuine not-found (the read guard folds an out-of-scope
    // caller into the same response) — retrying just delays the "not found"
    // state on screen.
    retry: false,
  });
}

/** The teacher's read of an event they are crewed on. Cached apart from the
 *  full detail because the server may leave the deal fields out. */
export function usePrivateEventCrewView(id: string) {
  return useQuery({
    queryKey: [...privateEventKey(id), 'crew-view'],
    queryFn: () => fetchPrivateEventCrewView(id),
    enabled: id.length > 0,
    staleTime: STALE_30S,
    retry: false,
  });
}

/** PUT /workshops/:id/staff via the shared crew editor, then refetch this
 *  private event's own detail so the page reflects the server's crew. */
export function useReplacePrivateEventCrew(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (staff: CrewSlotInput[]) => replaceWorkshopStaff(id, staff),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: privateEventKey(id) }),
        qc.invalidateQueries({ queryKey: ['private-event-history', id] }),
        qc.invalidateQueries({ queryKey: ['workshop', id] }),
      ]),
  });
}
